/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';
import type { PaymentFormValues } from 'Src/types/payment';
import React, { useContext, useEffect } from 'react';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import {
  HIPAY_PSP_ID,
  NAPAY_PSP_ID,
  PAYMENT_METHOD_CATEGORY,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import {
  isTransfer,
  isPayPal,
  handleClickPaymentProviderBlock,
  isCreditCard,
  isVoxpayProvider,
} from 'Components/widgets/payment-info/payment-form/paymentProvidersUtils';
import { t } from 'Src/i18n';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { useAppContext } from 'Context/ReactAppContext';
import { lowerFirst } from 'lodash';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { CreditCardPaymentBlock } from './CreditCardPaymentBlock';
import { GenericPaymentBlock } from './GenericPaymentBlock';
import { PaymentMethodsWrapper } from './PaymentMethodsWrapper';
import { PayPalPaymentBlock } from './PayPalPaymentBlock';
type Props = {
  hasFullAmount: boolean;
  isDisabled: boolean;
  orderedPaymentProviders: ReadonlyArray<PaymentProvider>;
  paymentSchedule: PaymentSchedule;
  rePaymentSchedule: RepaymentSchedule;
  shouldAutoSubmitForm: boolean;
  showPaymentMethodWithConditions: boolean;
  amountToPay: number;
  onProviderSelection?: (categoryPayment: string) => void;
};

const isPaymentProviderWithLogo = ({ categoryPaymentMethod }) =>
  [PAYMENT_METHOD_CATEGORY.BANK_TRANSFER, PAYMENT_METHOD_CATEGORY.PAYPAL].includes(
    categoryPaymentMethod,
  );

const PaymentProviderTitle = ({ paymentProvider }) => {
  let title = paymentProvider.description;

  if (paymentProvider.selectorId === NAPAY_PSP_ID) {
    title = t('booking.partners.paymentNA.title');
  }

  if (isPayPal(paymentProvider)) {
    title = t('booking.banking.paymentProvider.paypal.title');
  }

  return (
    <div className="flex items-start justify-between">
      <label
        key={paymentProvider.selectorId}
        data-testid={`PaymentProviders-${paymentProvider.selectorId}`}
        className={classnames('text-18', 'font-bold', 'text-black', 'pb-10')}
      >
        {title}
      </label>
      {isPaymentProviderWithLogo(paymentProvider) && paymentProvider.logo && (
        <img
          src={paymentProvider.logo}
          alt={`${paymentProvider.categoryPaymentMethod || ''} logo`}
        />
      )}
    </div>
  );
};

const BankTransferChecks = ({ isFramed }) => {
  return (
    <div
      className={classnames('mb-25 w-fit', {
        'border-1 border-sienna p-5': isFramed,
      })}
    >
      <div className="flex items-center py-2">
        <Icon className="text-sienna" name={ICONS.CHECK} width="1rem" />
        <span className="ms-10 text-12">
          {t('booking.banking.paymentProvider.bankTransfer.security')}
        </span>
      </div>
      <div className="flex items-center py-2">
        <Icon className="text-sienna" name={ICONS.CHECK} width="1rem" />
        <span className="ms-10 text-12">
          {t('booking.banking.paymentProvider.bankTransfer.paymentCap')}
        </span>
      </div>
    </div>
  );
};

export const PaymentProviders = ({
  hasFullAmount,
  isDisabled,
  orderedPaymentProviders,
  paymentSchedule,
  rePaymentSchedule,
  shouldAutoSubmitForm,
  amountToPay,
  onProviderSelection,
}: Props) => {
  const formik = useFormikContext<PaymentFormValues>();
  const { locale } = useAppContext();
  const { cardFeesPercentage, currency } = useContext(PaymentInfoContext);

  const handlePaymentBlockClick = ({
    paymentProvider,
    hasTimePaymentConditions,
  }: {
    paymentProvider: PaymentProvider;
    hasTimePaymentConditions: boolean;
  }) => {
    const isVoxpayPaymentProvider = isVoxpayProvider(paymentProvider.id);
    const hasPaymentMethods = !!paymentProvider.paymentMethods?.length;

    if (!hasPaymentMethods || hasTimePaymentConditions || isVoxpayPaymentProvider) {
      paymentProvider.categoryPaymentMethod &&
        onProviderSelection?.(paymentProvider.categoryPaymentMethod);
      handleClickPaymentProviderBlock({
        paymentProvider,
        formik,
        isDisabled,
        mustKeepPaymentMethod: isVoxpayPaymentProvider && hasPaymentMethods,
      });

      if (!isDisabled) {
        if (!isVoxpayPaymentProvider) {
          setTimeout(() => {
            scrollToElement('#HealthRegulationsTitleId', 100);
          }, 500);
        }
      }
    }
  };

  const isBankTransferChecksFramed =
    orderedPaymentProviders[0].id === HIPAY_PSP_ID &&
    orderedPaymentProviders[0].paymentMethods.length === 0;
  const { paymentProviderId } = formik.values;
  const paymentProviders = orderedPaymentProviders.map((provider) => ({
    ...provider,
    paymentMethods: provider.paymentMethods.length === 1 ? [] : provider.paymentMethods,
  }));
  const autoSelectPaymentMethodFns = orderedPaymentProviders.reduce(
    (fns, { paymentMethods, selectorId }) => {
      const shouldAutoSelectPaymentMethod = paymentMethods.length === 1;
      const paymentMethod = shouldAutoSelectPaymentMethod && paymentMethods[0];

      const autoSelectPaymentMethod = () => {
        if (paymentMethod) {
          formik.setFieldValue('paymentMethodId', paymentMethod.id);
          formik.setFieldValue('paymentConditionId', paymentMethod.id);
        }
      };

      fns[selectorId] = autoSelectPaymentMethod;
      return fns;
    },
    {},
  );
  useEffect(() => {
    autoSelectPaymentMethodFns[paymentProviderId]?.();
  }, [paymentProviderId]);
  const isCardFeesOnly = !!cardFeesPercentage;
  return (
    <div data-testid="Payment-method-block">
      {paymentProviders.map((paymentProvider, index) => {
        const hasNoPaymentMethods = paymentProvider.paymentMethods.length === 0;
        const borderBottom = index + 1 < orderedPaymentProviders.length && 'border-b-1';
        const cursorPointer = hasNoPaymentMethods && 'cursor-pointer';
        const isHipayPaymentProvider = paymentProvider.id === HIPAY_PSP_ID;
        const isPayPalPaymentProvider = isPayPal(paymentProvider);
        const feesRate =
          cardFeesPercentage ||
          Number(
            t(
              `booking.banking.paymentProvider.${lowerFirst(paymentProvider?.categoryPaymentMethod || '')}.feesRate`,
            ),
          ) ||
          0;
        const [totalAmount, depositAmount] = [amountToPay, paymentSchedule.depositAmount.value].map(
          (amount) =>
            formatAmountForLocale(amount, locale, currency, {
              precision: 2,
            }),
        );
        const [totalAmountWithFees, depositAmountWithFees] = [
          amountToPay,
          paymentSchedule.depositAmount.value,
        ].map((amount) =>
          formatAmountForLocale(amount * (1 + feesRate), locale, currency, {
            precision: 2,
          }),
        );
        const hasTimePaymentConditions = paymentProvider?.paymentMethods?.some(
          ({ timePaymentConditions }) => timePaymentConditions.length > 1,
        );
        const isGenericPaymentProvider = !isHipayPaymentProvider && !isPayPalPaymentProvider;
        const shouldDisplayPaymentMethods = !hasNoPaymentMethods && !hasTimePaymentConditions;
        const isProviderSelectable =
          isVoxpayProvider(paymentProvider.id) ||
          paymentProvider.paymentMethods.length <= 1 ||
          (hasTimePaymentConditions && orderedPaymentProviders.length > 1);
        const paymentBlockProps = {
          isDisabled,
          hasFullAmount,
          rePaymentSchedule,
          totalAmount,
          totalAmountWithFees,
          depositAmount,
          depositAmountWithFees,
          fees: feesRate ? `${feesRate * 100}%` : '',
        };
        return (
          <div
            className={classnames(
              'relative isolate m-0 flex flex-row-reverse',
              borderBottom,
              cursorPointer,
            )}
            data-testid={paymentProvider.selectorId}
            key={paymentProvider.selectorId}
            onClick={() =>
              handlePaymentBlockClick({
                paymentProvider,
                hasTimePaymentConditions,
              })
            }
          >
            {hasNoPaymentMethods && (isHipayPaymentProvider || isCreditCard(paymentProvider)) && (
              <Icon className="end-0 mx-20 mt-30" name={ICONS.CREDIT_CARD} width="5rem" />
            )}
            <div className="w-full p-20">
              <PaymentProviderTitle paymentProvider={paymentProvider} />
              {isHipayPaymentProvider && (
                <CreditCardPaymentBlock
                  {...paymentBlockProps}
                  paymentSchedule={paymentSchedule}
                  hasNoPaymentMethods={hasNoPaymentMethods}
                />
              )}
              {isPayPalPaymentProvider && (
                <PayPalPaymentBlock {...paymentBlockProps} paymentProvider={paymentProvider} />
              )}
              {isTransfer(paymentProvider) && (
                <BankTransferChecks isFramed={isBankTransferChecksFramed} />
              )}
              {isGenericPaymentProvider && (
                <GenericPaymentBlock
                  {...paymentBlockProps}
                  paymentProvider={paymentProvider}
                  isProviderSelectable={isProviderSelectable}
                  paymentMethodCategoryWithFees={
                    isCardFeesOnly
                      ? [PAYMENT_METHOD_CATEGORY.CREDIT_CARD]
                      : (Object.values(PAYMENT_METHOD_CATEGORY).map(String) as Array<string>)
                  }
                />
              )}
              {shouldDisplayPaymentMethods && (
                <PaymentMethodsWrapper
                  paymentProvider={paymentProvider}
                  shouldAutoSubmitForm={shouldAutoSubmitForm}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
